import { Fragment, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { MathText } from "@/components/math-text";
import type { ClozePassageContent, FillBlankContent, FillBlankSlot } from "../content-types";
import { blankMatches } from "../grading";
import { Explanation, Markup, Prompt } from "./shared";
import { GlossableText, isLanguageFramework } from "./glossable-text";
import type { QuestionRendererProps } from "./types";

/** Render the stem (template/passage), splitting on `___` blank markers and
 *  showing a numbered placeholder where each blank sits — so an equation stem
 *  like `\vec v(\vec k) = ___ \nabla_{\vec k} ___` renders as typeset math with
 *  the blanks called out, matching the "Blank N" pickers below. Each text
 *  segment goes through MathText, which renders prose as-is and KaTeXes any
 *  embedded LaTeX (so non-math, prose-with-blanks stems still read naturally). */
function StemWithBlanks({ text }: { text: string }) {
  // The generator sometimes wraps the marker as `\text{___}`; unwrap to a bare
  // `___` so the split lands cleanly.
  const cleaned = text.replace(/\\text\{\s*(_{2,})\s*\}/g, "$1");
  const segs = cleaned.split("___");
  return (
    <>
      {segs.map((seg, i) => (
        <Fragment key={i}>
          {seg && <MathText text={seg} />}
          {i < segs.length - 1 && (
            <span className="mx-0.5 inline-flex items-center rounded-md border border-dashed border-brand-300 bg-brand-50 px-1.5 py-0.5 align-middle text-xs font-semibold text-brand-600">
              {i + 1}
            </span>
          )}
        </Fragment>
      ))}
    </>
  );
}

function shuffled<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/** Renders fill_blank (template) and cloze_passage (passage). When a blank has
 *  distractors, the learner picks from a shuffled WORD-BANK (no messy typing);
 *  otherwise types (graded fuzzily). LaTeX renders via MathText. */
export function FillBlankRenderer({
  type,
  content,
  answer,
  setAnswer,
  revealed,
  frameworkSlug,
  translateAnswers,
}: QuestionRendererProps) {
  const { t } = useTranslation();
  const c = content as unknown as FillBlankContent & ClozePassageContent;
  const enableGloss = isLanguageFramework(frameworkSlug);
  const enableAnswerGloss = enableGloss && !!translateAnswers;
  const blanks: FillBlankSlot[] = useMemo(
    () => [...(c.blanks ?? [])].sort((a, b) => a.index - b.index),
    [c.blanks],
  );
  const context = type === "cloze_passage" ? c.passage : c.template;
  const values = (answer as string[]) ?? [];

  // Per-blank word-bank (correct + distractors), shuffled once.
  const banks = useMemo(
    () =>
      blanks.map((b) =>
        b.distractors && b.distractors.length > 0 ? shuffled([b.correct, ...b.distractors]) : null,
      ),
    [blanks],
  );

  const setAt = (i: number, val: string) => {
    const next = [...values];
    next[i] = val;
    setAnswer(next);
  };

  const isCorrectOpt = (b: FillBlankSlot, opt: string) =>
    opt === b.correct || (b.accepted_alternatives ?? []).includes(opt);

  return (
    <div>
      <Prompt>{t("Fill in the blanks")}</Prompt>
      {context && (
        <div className="mt-3 whitespace-pre-wrap rounded-xl bg-black/[0.03] p-3.5 text-sm leading-relaxed text-ink-soft">
          {enableGloss ? <GlossableText text={context} context={context} /> : <StemWithBlanks text={context} />}
        </div>
      )}
      <div className="mt-4 space-y-3">
        {blanks.map((b, i) => {
          const ok = revealed ? blankMatches(b, values[i] ?? "") : null;
          const bank = banks[i];
          return (
            <div key={b.index}>
              <div className="mb-1 flex items-center gap-2 text-xs font-medium text-ink-faint">
                <span>{t("Blank {{n}}", { n: i + 1 })}</span>
                {b.competence && <span className="rounded bg-black/5 px-1.5 py-0.5">{b.competence}</span>}
              </div>
              {bank ? (
                <div className="flex flex-wrap gap-2">
                  {bank.map((opt, k) => {
                    const chosen = values[i] === opt;
                    const correct = revealed && isCorrectOpt(b, opt);
                    return (
                      <button
                        key={k}
                        type="button"
                        disabled={revealed}
                        onClick={() => setAt(i, opt)}
                        className={`rounded-full border px-3 py-1.5 text-sm transition ${
                          revealed
                            ? correct
                              ? "border-emerald-400 bg-emerald-50 text-emerald-800"
                              : chosen
                                ? "border-red-400 bg-red-50 text-red-700"
                                : "border-black/10 bg-white text-ink-faint"
                            : chosen
                              ? "border-brand-500 bg-brand-50 text-brand-800"
                              : "border-black/10 bg-white hover:bg-brand-50"
                        }`}
                      >
                        {enableAnswerGloss ? (
                          <GlossableText text={opt} context={context} />
                        ) : (
                          <Markup>{opt}</Markup>
                        )}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <input
                  value={values[i] ?? ""}
                  disabled={revealed}
                  onChange={(e) => setAt(i, e.target.value)}
                  className={`w-full rounded-xl border px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400 ${
                    revealed
                      ? ok
                        ? "border-emerald-400 bg-emerald-50"
                        : "border-red-400 bg-red-50"
                      : "border-black/10"
                  }`}
                />
              )}
              {revealed && !ok && (
                <p className="mt-1 text-xs text-emerald-700">
                  {t("Answer:")}{" "}
                  <span className="font-semibold">
                    <Markup>{b.correct}</Markup>
                  </span>
                </p>
              )}
            </div>
          );
        })}
      </div>
      {revealed && <Explanation text={c.explanation} />}
    </div>
  );
}
